import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Menu, X, Phone } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/gallery", label: "Gallery" },
    { path: "/booking", label: "Book Appointment" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-12 h-12 bg-blue-600 rounded-sm flex items-center justify-center">
              <span className="text-white font-black text-xl font-['Chivo']">875</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-black text-xl text-slate-900 font-['Chivo']">875 Construction Corp</span>
              <p className="text-xs text-slate-500 font-medium tracking-wide">WINDOWS • DOORS • KITCHENS • BATHS • NEW CONSTRUCTION</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link font-medium transition-colors ${
                  isActive(link.path) 
                    ? "text-blue-600" 
                    : "text-slate-600 hover:text-blue-600"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href="tel:708-856-1055" 
              className="flex items-center gap-2 text-slate-600 hover:text-blue-600 font-medium transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>708-856-1055</span>
            </a>
            
            <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold px-6 py-2 btn-primary"
                  data-testid="get-quote-btn"
                >
                  Get a Quote
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold font-['Chivo']">Request a Free Quote</DialogTitle>
                </DialogHeader>
                <QuoteForm onSuccess={() => setQuoteDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-600 hover:text-blue-600 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200 animate-fade-in" data-testid="mobile-nav">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-4 px-4 flex flex-col gap-3">
                <a 
                  href="tel:708-856-1055" 
                  className="flex items-center gap-2 text-slate-600 font-medium"
                >
                  <Phone className="w-4 h-4" />
                  <span>708-856-1055</span>
                </a>
                <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold w-full"
                      data-testid="mobile-get-quote-btn"
                    >
                      Get a Quote
                    </Button>
                  </DialogTrigger>
                </Dialog>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
